package twist.mena.sports.admin.api

import io.ktor.client.*
import io.ktor.client.call.*
import io.ktor.client.plugins.contentnegotiation.*
import io.ktor.client.request.*
import io.ktor.client.statement.*
import io.ktor.http.*
import io.ktor.serialization.kotlinx.json.*
import kotlinx.serialization.json.Json
import kotlinx.serialization.json.jsonObject
import kotlinx.serialization.json.jsonPrimitive
import twist.mena.sports.admin.state.AuthState
import twist.mena.sports.admin.state.NavState
import twist.mena.sports.admin.state.Screen
import twist.mena.sports.shared.dto.admin.AdminNotificationDisplayItem
import twist.mena.sports.shared.dto.admin.AdminNotificationEventType
import twist.mena.sports.shared.dto.admin.AdminNotificationGroup
import twist.mena.sports.shared.dto.admin.AdminNotificationHistoryResponse
import twist.mena.sports.shared.dto.admin.AdminNotificationSettingsResponse
import twist.mena.sports.shared.dto.admin.AdminTemplatePreviewRequest
import twist.mena.sports.shared.dto.admin.AdminTemplatePreviewResponse
import twist.mena.sports.shared.dto.admin.OffsetPage

/** Filters of the History tab; blank values are not sent. */
data class HistoryFilters(
    val source: String = "",
    val group: String = "",
    val eventCode: String = "",
    val status: String = "",
    val entityType: String = "",
    val entityRef: String = "",
    val from: String = "",
    val to: String = "",
    val query: String = ""
)

class AdminNotificationSettingsApi(private val baseUrl: String) {

    private val json = Json {
        ignoreUnknownKeys = true
        isLenient = true
    }

    private val client = HttpClient {
        install(ContentNegotiation) { json(json) }
    }

    private val base = "$baseUrl/api/v1/admin/notifications"

    private fun authHeaders(): HeadersBuilder.() -> Unit = {
        val token = AuthState.getToken()
        if (token != null) {
            append(HttpHeaders.Authorization, "Bearer $token")
        }
    }

    /** Logs out on 401; otherwise surfaces the server's problem detail on any non-2xx. */
    private suspend fun check(response: HttpResponse) {
        if (response.status == HttpStatusCode.Unauthorized) {
            AuthState.logout()
            NavState.navigate(Screen.LOGIN)
        }
        if (!response.status.isSuccess()) {
            val detail = try {
                json.parseToJsonElement(response.bodyAsText()).jsonObject["detail"]?.jsonPrimitive?.content
            } catch (_: Exception) { null }
            throw Exception(detail ?: "Request failed: ${response.status.value}")
        }
    }

    suspend fun getSettings(): AdminNotificationSettingsResponse {
        val response = client.get("$base/settings") { headers(authHeaders()) }
        check(response)
        return response.body()
    }

    suspend fun updateGroup(group: AdminNotificationGroup): AdminNotificationGroup {
        val response = client.put("$base/settings/groups/${group.code}") {
            headers(authHeaders())
            contentType(ContentType.Application.Json)
            setBody(group)
        }
        check(response)
        return response.body()
    }

    suspend fun saveDisplayItem(item: AdminNotificationDisplayItem): AdminNotificationDisplayItem {
        val response = client.put("$base/settings/display-items/${item.code}") {
            headers(authHeaders())
            contentType(ContentType.Application.Json)
            setBody(item)
        }
        check(response)
        return response.body()
    }

    suspend fun saveEventType(eventType: AdminNotificationEventType): AdminNotificationEventType {
        val response = client.put("$base/settings/event-types/${eventType.code}") {
            headers(authHeaders())
            contentType(ContentType.Application.Json)
            setBody(eventType)
        }
        check(response)
        return response.body()
    }

    suspend fun previewTemplates(request: AdminTemplatePreviewRequest): AdminTemplatePreviewResponse {
        val response = client.post("$base/settings/event-types/preview") {
            headers(authHeaders())
            contentType(ContentType.Application.Json)
            setBody(request)
        }
        check(response)
        return response.body()
    }

    suspend fun getHistory(filters: HistoryFilters, offset: Int, limit: Int = 50): OffsetPage<AdminNotificationHistoryResponse> {
        val response = client.get("$base/history") {
            headers(authHeaders())
            parameter("offset", offset)
            parameter("limit", limit)
            fun optional(name: String, value: String) { if (value.isNotBlank()) parameter(name, value.trim()) }
            optional("source", filters.source)
            optional("group", filters.group)
            optional("event_code", filters.eventCode)
            optional("status", filters.status)
            optional("entity_type", filters.entityType)
            optional("entity_ref", filters.entityRef)
            optional("from", filters.from)
            optional("to", filters.to)
            optional("q", filters.query)
        }
        check(response)
        return response.body()
    }
}
